import {APP_INITIALIZER, Injectable, Renderer2, RendererFactory2} from '@angular/core'
import {Store} from '@ngrx/store'
import {OrderEntry} from '@spartacus/cart/base/root'
import {StateWithAnonymousConsents, StateWithProcess, StateWithUser, UsersSelectors} from '@spartacus/core'
import {CookieService} from 'ngx-cookie-service'
import {matomoScript, matomoTagManagerScript} from 'public/analytics/matomo-analytics'
import {combineLatest} from 'rxjs'
import {distinctUntilChanged, map, take} from 'rxjs/operators'
import {EbCart} from 'src/app/features/cart/interface/eb-cart.interface';
import {EbAuthService} from '../../../auth/services/eb-auth.service'
import {EbOrderEntry} from "../../../cart/interface/eb-order-entry.interface";
import {EbOrder} from "../../../order/interfaces/eb-order.interface";
import {EBBreadcrumb, EbProduct, EbProductSearchPage} from "../../../product/interfaces/eb-product.interface";
import {EbBaseSite} from '../../../site-context/interfaces/eb-site-context.interface'
import {EbBaseSiteService} from '../../../site-context/services/eb-base-site.service'
import {EbAnalyticsWindowRef} from '../../interface/eb-analytics.interface'
import {EbAnalyticsAdapter} from '../connectors/eb-analytics.adapter'

@Injectable({ providedIn: 'root' })
export class EbAnalyticsService {
  currentActiveContext: EbBaseSite
  paq = '_paq'
  lastNavigationEndUrl: string
  lastResolveEndUrl: string
  lastScrollUrl: string

  constructor(
    protected rendererFactory: RendererFactory2,
    protected winRef: EbAnalyticsWindowRef,
    protected cookieService: CookieService,
    protected store: Store<StateWithUser | StateWithProcess<void>>,
    protected anonymousStore: Store<StateWithAnonymousConsents>
  ) {}

  static get initializeProvider() {
    return {
      provide: APP_INITIALIZER,
      useFactory: EbAnalyticsService.setupAnalytics,
      multi: true,
      deps: [EbAnalyticsAdapter, EbBaseSiteService, EbAnalyticsService, EbAuthService]
    }
  }

  private static setupAnalytics(
    gaAdapter: EbAnalyticsAdapter,
    baseSiteService: EbBaseSiteService,
    analyticsService: EbAnalyticsService,
    authService: EbAuthService
  ) {
    return () => {
      gaAdapter
        .getAnalyticsSystemId()
        .pipe(take(1))
        .subscribe(systemIds => {
          if (systemIds.analyticsTrackingId) {
            combineLatest([baseSiteService.getActiveContext(), authService.isUserLoggedIn()])
              .pipe(
                distinctUntilChanged((oldValue, newValue) => {
                  return JSON.stringify(oldValue) === JSON.stringify(newValue)
                }),
                map(([activeContext, isUserLoggedIn]) => {
                  if (activeContext.enableGoogleTracking) {
                    if (systemIds.tagManagerContainerId) {
                      analyticsService.addTagManager(systemIds.tagManagerContainerId);
                    } else {
                      console.warn('Tag Manager ID is empty');
                    }
                    if (isUserLoggedIn) {
                      analyticsService.initializeAnalyticsForLoggedInUsers(
                        activeContext,
                        systemIds.analyticsTrackingId
                      );
                    } else {
                      analyticsService.initializeAnalyticsForAnonymousUsers(
                        systemIds.analyticsTrackingId
                      );
                    }
                  } else {
                    analyticsService.removeAnalytics()
                  }
                })
              )
              .subscribe()
          } else {
            console.warn('System ID is empty')
          }
        })
    }
  }

   initializeAnalyticsForLoggedInUsers(activeContext: EbBaseSite, systemId: string) {
    if (this.accessToken) {
      this.store
        .select(
          UsersSelectors.getConsentByTemplateId(`ANALYTICS-${activeContext.uid.split('-')[1]}`)
        )
        .pipe(
          map(consent => {
            if (!!consent) {
              const isConsentGiven =
                consent.currentConsent && !consent.currentConsent.consentWithdrawnDate
              this.handleAnalyticsPermission(isConsentGiven, systemId)
            } else {
              this.removeAnalytics()
            }
          })
        )
        .subscribe()
    }
  }

  initializeAnalyticsForAnonymousUsers(systemId: string) {
    this.handleAnalyticsPermission(true, systemId)
  }

  private handleAnalyticsPermission(isConsentGiven: boolean, systemId: string) {
    isConsentGiven ? this.addAnalytics(systemId) : this.removeAnalytics()
  }

  private get renderer(): Renderer2 {
    return this.rendererFactory.createRenderer(null, null)
  }

  private get scriptElementsInHead() {
    return this.winRef.document.querySelectorAll(
      'script[src^="https://cdn.matomo.cloud/myeagleburgmann.matomo.cloud/matomo.js"]'
    )
  }

  private get containerElementsInHead() {
    return this.winRef.document.querySelectorAll(
      'script[src^="https://cdn.matomo.cloud/myeagleburgmann.matomo.cloud/container_"]'
    )
  }

  addAnalytics(systemId: string) {
    if (!this.scriptElementsInHead.length) {
      const newScript = this.renderer.createElement('script')
      newScript.type = 'text/javascript'
      newScript.text = matomoScript.replace('{$IDSITE}', "'" + systemId + "'")
      this.renderer.appendChild(this.winRef.document.head, newScript)
    }
  }

  addTagManager(systemId: string) {
    if (!this.containerElementsInHead.length) {
      const newScript = this.renderer.createElement('script')
      newScript.type = 'text/javascript'
      newScript.text = matomoTagManagerScript.replace('{$idContainer}',  systemId)
      this.renderer.appendChild(this.winRef.document.head, newScript)
    }
  }

  removeAnalytics() {
    if (this.scriptElementsInHead.length > 0) {
      this.scriptElementsInHead.forEach(element => element.remove())
    }
    const analyticsCookies = Object.keys(this.cookieService.getAll()).filter(cookie =>
      cookie.startsWith('_pk')
    )
    if (analyticsCookies.length > 0) {
      analyticsCookies.forEach(analyticsCookie => this.cookieService.delete(analyticsCookie, '/'))
    }
  }

  get accessToken() {
    return (
      this.cookieService.get('access_token') ||
      this.cookieService.get('temp_customer_emulation_token')
    )
  }

 addQuickOrderEntries(entry: OrderEntry): void {
   const _paq = this.winRef.nativeWindow[this.paq] || [];
   if(_paq){
       _paq.push(['trackEvent', 'Ecommerce', 'Quick-add-to-cart', 'Line item',  entry.totalPrice?.value ]);
   }
  }

  removeEcommerceItem(productsPrices: string, entries: EbOrderEntry[]) {
    const _paq = this.winRef.nativeWindow[this.paq] || [];
    if(_paq){
      _paq.push(['trackEvent', 'Ecommerce', 'Remove-from-cart', 'Delete whole position', productsPrices ]);
      _paq.push(['removeEcommerceItem', productsPrices])
      this.pushTrackEcommerceCartUpdate(this.calculateTotalPrice(entries))
    }
  }

  addToFavorites(productPrice: string) {
    const _paq = this.winRef.nativeWindow[this.paq] || [];
    if(_paq){
      _paq.push(['trackEvent', 'PDP', 'Add-to-favorites', 'Favorites',  productPrice ]);
    }
  }

  addEcommerceProductToCart(entries: EbOrderEntry[], productPrice: string, category: string, action: string, eventName: string) {
    const _paq = this.winRef.nativeWindow[this.paq] || [];
    if(_paq){
      _paq.push(['clearEcommerceCart']);
      if(productPrice){
        _paq.push(['trackEvent', category, action, eventName,  productPrice ]);
      }
      entries.forEach((entry: EbOrderEntry) => {
        _paq.push(['addEcommerceItem',
          entry.product.code,
          entry.product.parentProductName,
          this.extractCategoriesFromBreadcrumbs(entry.product.breadcrumbs),
          entry.basePrice.value,
          entry.quantity,
        ]);
      })
      const totalPrice = this.calculateTotalPrice(entries)
      this.pushTrackEcommerceCartUpdate(totalPrice)
    }
  }

  requestForQuotation(eventName: string) {
    const _paq = this.winRef.nativeWindow[this.paq] || [];
    if(_paq){
      _paq.push(['trackEvent', 'RFP', 'Click on RfP', eventName,  "0" ]);
    }
  }

  pushCustomUrl(url: string){
    const _paq = this.winRef.nativeWindow[this.paq] || [];
    if(_paq){
      _paq.push(['setCustomUrl', url]);
      _paq.push(['trackPageView']);
      _paq.push(['enableLinkTracking']);
    }
  }

  placeOrder(order: EbOrder){
    const net = (order as EbCart).cartSubTotals.filter(price => price.textId === "prices.PR_SUB_NET_VALUE").map(price => price.value)[0];
    const tax = (order as EbCart).cartSubTotals.filter(price => price.textId === "prices.PR_SUB_TAX_VALUE").map(price => price.value)[0];
    const _paq = this.winRef.nativeWindow[this.paq] || [];
    if(_paq){
      _paq.push(['trackEcommerceOrder',
        order.code,
        order.totalPriceWithTax.value,
        net,
        tax,
        0,
        false
      ]);
      _paq.push(['clearEcommerceCart']);
      this.pushTrackEcommerceCartUpdate(net);
      _paq.push(['trackEvent', 'Ecommerce', 'Purchase', 'Purchase',  net]);
    }
  }

  pushTrackEcommerceCartUpdate(total: number){
    const _paq = this.winRef.nativeWindow[this.paq] || [];
    if(_paq) {
      _paq.push(['trackEcommerceCartUpdate', total]);
    }
  }

  pushViewVariantProductEvents(product:EbProduct, price: number){
    const _paq = this.winRef.nativeWindow[this.paq] || [];
    if(_paq){
      _paq.push(['setEcommerceView',
         product.code, // (Required) productSKU
         product.parentProductName, // (Optional) productName
         this.extractCategoriesFromBreadcrumbs(product.breadcrumbs), // (Optional) categoryName
         price, // (Optional) price
      ]);
    }
  }

  pushViewParentProductEvents(product:EbProduct){
    const _paq = this.winRef.nativeWindow[this.paq] || [];
    if(_paq){
      _paq.push(['setEcommerceView',
       "false", // (Required) productSKU
        product.name, // (Optional) productName
        this.extractCategoriesFromBreadcrumbs(product.breadcrumbs), // (Optional) categoryName
      ]);
    }
  }

  pushViewCategoryEvents(category:EbProductSearchPage){
    const _paq = this.winRef.nativeWindow[this.paq] || [];
    if(_paq){
      _paq.push(['setEcommerceView',
        "false", // (Required) productSKU
        "false", // (Optional) productName
        this.extractCategoriesFromBreadcrumbs(category.breadcrumbs), // (Optional) categoryName
      ]);
    }
  }

  extractCategoriesFromBreadcrumbs(breadcrumbs: EBBreadcrumb[]): string[]{
    return breadcrumbs?.slice(0, 4).map(breadcrumb => breadcrumb.facetNameEn ? breadcrumb.facetNameEn : breadcrumb.facetValueName);
  }

  calculateTotalPrice(entries: EbOrderEntry[]): number{
    let total = 0;
    entries.forEach(entry => {
      total += entry.totalPrice.value;
    })
    return total
  }

  pushIncrementProductEvent(productPrice: string, eventName: string){
    const _paq = this.winRef.nativeWindow[this.paq] || [];
    if(_paq) {
    _paq.push(['trackEvent', 'Ecommerce', 'Add-to-Cart', eventName,  productPrice]);
    }
  }

  pushDecrementProductEvent(productPrice: string, eventName: string){
    const _paq = this.winRef.nativeWindow[this.paq] || [];
    if(_paq) {
    _paq.push(['trackEvent', 'Ecommerce', 'Remove-from-Cart', eventName,  productPrice]);
    }
  }
}
